import {getImageUrl} from '@cohort/shared/utils/media';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import PdfDownloadCard from '@cohort/wallet/components/card/PdfDownloadCard';
import {VideoPlayer} from '@cohort/wallet/components/card/VideoPlayer';
import {Loader} from '@cohort/wallet/components/Loader';
import {Subtitle} from '@cohort/wallet/components/Subtitle';
import {useGeneratePrivateContentUrl} from '@cohort/wallet/hooks/api/Ownerships';
import {useCohortMutation} from '@cohort/wallet/hooks/api/Query';
import {useApi} from '@cohort/wallet/hooks/useApi';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import {OwnershipsApi} from '@cohort/wallet/lib/Endpoints';
import {Colors} from '@cohort/wallet/lib/Theme';
import type {PerkWDto} from '@cohort/wallet-schemas/perk';
import {PlayCircle} from '@phosphor-icons/react';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {match} from 'ts-pattern';

const PerkDetailPageVideo: React.FC<{
  ownershipId: string;
  perkAccessId: string;
  perk: PerkWDto;
}> = ({ownershipId, perkAccessId, perk}) => {
  const {
    data: privateContentUrlResponse,
    isFetching,
    isError,
  } = useGeneratePrivateContentUrl(ownershipId, perkAccessId, perk.privateContentFileType ?? '');

  const {t} = useTranslation('pages', {keyPrefix: 'space.perkDetailPage'});

  return match({isFetching, isError})
    .with({isFetching: true}, () => (
      <div className="relative h-10 w-10">
        <Loader color={Colors.DarkGray} />
      </div>
    ))
    .with({isError: true}, () => (
      <div className="relative h-10 w-10">{t('privateVideoCannotLoad')}</div>
    ))
    .otherwise(() => (
      <VideoPlayer
        displayName={'video'}
        autoPlay={true}
        imgUrl={getImageUrl(import.meta.env.COHORT_ENV, perk.privateContentThumbnailFileKey ?? '')}
        videoFileKey={privateContentUrlResponse?.url ?? ''}
        className="w-full max-w-3xl"
      />
    ));
};

const PerkDetailPageVideoWrapper: React.FC<{
  ownershipId: string;
  perkAccessId: string;
  perk: PerkWDto;
}> = ({ownershipId, perkAccessId, perk}) => {
  const [clicked, setClicked] = useState(false);

  return (
    <div className="overflow-hidden">
      {clicked ? (
        <PerkDetailPageVideo ownershipId={ownershipId} perkAccessId={perkAccessId} perk={perk} />
      ) : (
        <div className="relative w-full max-w-3xl overflow-clip rounded-xl">
          <img
            src={getImageUrl(import.meta.env.COHORT_ENV, perk.privateContentThumbnailFileKey ?? '')}
            alt="Click here to access your content"
            className="object-cover"
          />
          <div className="absolute bottom-0 left-0 right-0 top-0 flex h-full w-full items-center justify-center">
            <PlayCircle className="h-16 w-16 cursor-pointer" onClick={() => setClicked(true)} />
          </div>
        </div>
      )}
    </div>
  );
};

const PerkDetailPagePdf: React.FC<{
  ownershipId: string;
  perkAccessId: string;
}> = ({ownershipId, perkAccessId}) => {
  const generatePrivateContentUrl = useApi(OwnershipsApi.generatePrivateContentUrl);
  const {isLoading, mutate: doGeneratePrivateContentUrlMutation} = useCohortMutation({
    mutationFn: async () => generatePrivateContentUrl(ownershipId, perkAccessId),
    onSuccess: privateContentUrlResponse => {
      window.open(privateContentUrlResponse.url, '_top');
    },
  });

  return (
    <PdfDownloadCard
      onDownloadClick={() => doGeneratePrivateContentUrlMutation()}
      isLoading={isLoading}
    />
  );
};

const PerkDetailPrivateContent: React.FC<{
  ownershipId: string;
  perkAccessId: string;
  perk: PerkWDto;
}> = ({ownershipId, perkAccessId, perk}) => {
  const {t} = useTranslation('pages', {keyPrefix: 'space.perkDetailPage'});
  const {hasDarkBg} = useThemeContext();

  const subtitleClassName = classNames(
    'text-2xl font-semibold mb-4',
    hasDarkBg ? 'text-white' : 'text-black'
  );

  const hasContent = !!perk.privateContent;
  const isVideo = perk.privateContentFileType === 'video';
  const isPdf = perk.privateContentFileType === 'pdf';

  return (
    <div>
      {perk.privateContent && (
        <div>
          <Subtitle classes={subtitleClassName} content={t('labelInformation')} />

          <div
            dangerouslySetInnerHTML={{__html: perk.privateContent}}
            className={classNames('prose prose-sm text-left', hasDarkBg && 'prose-invert')}
          />
        </div>
      )}
      {isVideo && (
        <div className={classNames(hasContent && 'mt-8')}>
          <Subtitle classes={subtitleClassName} content={t('labelVideos')} />

          <PerkDetailPageVideoWrapper
            ownershipId={ownershipId}
            perkAccessId={perkAccessId}
            perk={perk}
          />
        </div>
      )}

      {isPdf && (
        <div className={classNames('flex flex-col items-center space-y-4', hasContent && 'mt-8')}>
          <Subtitle classes={subtitleClassName} content={t('labelFiles')} />

          <PerkDetailPagePdf ownershipId={ownershipId} perkAccessId={perkAccessId} />
        </div>
      )}
    </div>
  );
};

export default PerkDetailPrivateContent;
